// @flow
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Slider from 'react-slick';
import { defineMessages, FormattedMessage, intlShape } from 'react-intl';
import classNames from 'classnames/bind';
import { Default, Mobile } from '../../../utils/responsive';
import { getLocalizedCountry } from '../../local-values/utils/countries';
import CarouselImages from '../../../elements/CarouselImages/CarouselImages';
import ScrapInputBlock from './ScrapInputBlock/ScrapInputBlock';
import Arrow from '../../../elements/Arrow/Arrow';
import { urlVerifier } from '../../../utils/string';

import style from './GenericStore.scss';
import icons from '../../../assets/styles/icons.scss';
import btnStyle from '../../../assets/styles/button.scss';
import step1 from '../../../assets/img/stores/steps/info1.png';
import step2 from '../../../assets/img/stores/steps/info2.png';
import step3 from '../../../assets/img/stores/steps/info3.png';
import visa from '../../../assets/img/new-order/payment-method/visa.png';
import mastercard from '../../../assets/img/new-order/payment-method/mastercard.png';
import paypal from '../../../assets/img/new-order/payment-method/paypal.png';
import bitcoin from '../../../assets/img/new-order/payment-method/bitcoin.png';
import bcp from '../../../assets/img/new-order/payment-method/BCP.png';
import interbank from '../../../assets/img/new-order/payment-method/interbank.png';
import bbva from '../../../assets/img/new-order/payment-method/bbva.png';
import scotiabank from '../../../assets/img/new-order/payment-method/Scotiabank.png';
import american from '../../../assets/img/new-order/payment-method/american.png';
import kasnet from '../../../assets/img/new-order/payment-method/kasnet.png';
import arequipa from '../../../assets/img/new-order/payment-method/caja_arequipa.png';
import pse from '../../../assets/img/new-order/payment-method/PSE.png';
import colombiaBank from '../../../assets/img/new-order/payment-method/bancolombia.png';
import bogotabank from '../../../assets/img/new-order/payment-method/banco_bogota.png';
import villas from '../../../assets/img/new-order/payment-method/av_villas.png';
import occidentebank from '../../../assets/img/new-order/payment-method/banco_occidente.png';
import davivienda from '../../../assets/img/new-order/payment-method/davivienda.png';
import popularBank from '../../../assets/img/new-order/payment-method/banco_popular.png';

import type { OnlineStore } from '../../../entities';
import type { Node } from 'react';
import type { Match } from '../../../router';
import type { LocaleState } from '../../locale/duck';
import type { LocalValuesState } from '../../local-values/duck';

type Props = {
  +store?: OnlineStore;
  +match: Match;
  +locale: LocaleState;
  +localValues: LocalValuesState;
  +goToRedirect: (route: string) => void;
};

const steps = [
  {
    img: step1,
    text: 'stores.carousel.step1.text',
  },
  {
    img: step2,
    text: 'stores.carousel.step2.text',
  }, {
    img: step3,
    text: 'stores.carousel.step3.text',
  }
];

const messages = defineMessages({
  titlePartOne: {
    id: 'store.carousel.title.partOne',
  },
  titlePartTwo: {
    id: 'store.carousel.title.partTwo',
  },
});

const methods = {
  PE: [
    visa,
    mastercard,
    paypal,
    bitcoin,
    bcp,
    interbank,
    bbva,
    scotiabank,
    american,
    kasnet,
    arequipa,
  ],
  US: [
    visa,
    mastercard,
    paypal,
    bitcoin,
    american,
  ],
  CO: [
    visa,
    mastercard,
    bitcoin,
    pse,
    colombiaBank,
    bogotabank,
    villas,
    bbva,
    occidentebank,
    davivienda,
    popularBank,
    paypal,
    american,
  ]
};

const settings = {
  dots: true,
  arrows: true,
  draggable: true,
  slidesToShow: 1,
  slidesToScroll: 1,
  infinite: false,
  dotsClass: classNames('slick-dots', style.dots),
  nextArrow: <Arrow icon={icons.iconChevronRightMedium}
                    positionClass={style.arrowRight}
                    visible/>,
  prevArrow: <Arrow icon={icons.iconChevronLeftMedium}
                    positionClass={style.arrowLeft}
                    visible/>
};

export default class GenericStore extends Component<Props> {
  
  buildInfos(): Node[] {
    const { store } = this.props;
    return !store ? [] : steps.map((step, index: number) => (
      <article className={style.step}
               key={index}>
        <section className={style.picture}>
          <img className={style.backImage} src={step.img} alt="Qempo"/>
          {index !== 2 && <img className={style.logoStore}
                               src={store.logo}
                               alt={store.name}/>}
          <div className={style.number}>{index + 1}</div>
        </section>
        <FormattedMessage id={step.text}>{
          text => <p>{text}</p>
        }</FormattedMessage>
      </article>)
    );
  }

  render() {
    const { store, locale, goToRedirect, localValues } = this.props;
    return (
      <section className={style.mainWrap}>
        <section className={style.topBanner}>
          <section className={style.content}>
            <h1 className={style.title}>{store && this.context.intl.formatMessage(messages.titlePartOne)}
              <span className={style.store}>{store && ` ${store.name}`}</span>
              {store && locale.domainCountry !== 'US' && ` ${this.context.intl.formatMessage(messages.titlePartTwo)} `}
              {store && locale.domainCountry !== 'US' &&
              <span className={style.country}>
                {getLocalizedCountry(locale.domainCountry || 'PE', this.context.intl.formatMessage, localValues.countriesMap)}
              </span>}
            </h1>
            <section className={style.carousel}>
              <Mobile>
                <Slider ref="slick" {...settings}>
                  {this.buildInfos()}
                </Slider>
              </Mobile>
              <Default>
                {this.buildInfos()}
              </Default>
            </section>
            {store && <FormattedMessage id="stores.carousel.button">{
              text => <a className={classNames(btnStyle.btn, style.whiteInverse)}
                         href={`${store.url}`}
                         target="_blank">{`${text} ${store.name}`}</a>
            }</FormattedMessage>}
          </section>
        </section>
        <ScrapInputBlock handleClickButton={(input: string) => {
          if (urlVerifier(input) || input.startsWith('http')) {
            goToRedirect(`/new-order/product?initialQuery=${input}`);
          } else {
            goToRedirect(`/new-order/search?initialQuery=${input}`);
          }
        }}/>
        <section className={style.paymentsWrap}>
          <CarouselImages items={locale && methods[locale.domainCountry || 'PE']}
                          title="newOrder.scrap.paymentMethods.title"/>
        </section>
      </section>
    );
  }
}

GenericStore.propTypes = {
  store: PropTypes.object,
  match: PropTypes.object.isRequired,
  locale: PropTypes.object.isRequired,
  localValues: PropTypes.object.isRequired,
  goToRedirect: PropTypes.func.isRequired,
};

GenericStore.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/modules/store/GenericStore/GenericStore.jsx